import axios from 'axios';
import { refreshTokenDev, refreshTokenProd } from './login/login.api';
import { createBrowserRouter } from 'react-router-dom';
import { useAuthStore } from '../zustand-store/auth.store';
import { toast } from 'react-toastify';

const BASE_URL = import.meta.env.VITE_BASE_URL_BACKEND;

const router = createBrowserRouter(['/login']);
// Create an instance of axios
const axiosInstance = axios.create({
  baseURL: BASE_URL, // Replace with your API base URL
  timeout: 10000, // Request timeout
  headers: {
    'Content-Type': 'application/json'
  }
});

// A page fires several requests at once, and an expired session means all of
// them fail together. These two module-level values make sure that pile-up
// still produces exactly one refresh attempt and one redirect, not one per
// failed request.
let refreshPromise = null;
let sessionExpiryHandled = false;

const SESSION_EXPIRED_STORAGE_KEY = 'sessionExpiredNotice';

// AccessTokenMiddlware (backend) used to send its 401s as a raw text body
// instead of the app's usual { message: '...' } JSON shape, which meant this
// exact check could never match. It now sends JSON too, but keep this
// tolerant of both in case an environment is still running the older build.
function extractErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof data === 'string') return data;
  return data?.message;
}

function redirectToLoginOnSessionExpiry() {
  if (sessionExpiryHandled) return;
  sessionExpiryHandled = true;
  try {
    sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, '1');
  } catch (e) {
    /* no-op */
  }
  toast.error('Your session has expired. Please log in again.', {
    toastId: 'session-expired'
  });
  window.location.href = '/login';
}

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // You can add headers here, for example, adding an authorization token
    const token = useAuthStore.getState().accessToken;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    // Handle request error
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    console.log(error, '=error====');

    // Any status codes that falls outside the range of 2xx causes this function to trigger
    if (error.response) {
      const message = extractErrorMessage(error);

      if (message != 'Token expired') {
        if (Array.isArray(message)) {
          // If the message is an array, display each message as a separate toast
          message.forEach((msg) => {
            if (typeof msg === 'string') {
              toast.error(msg);
            }
          });
        } else if (typeof message === 'string') {
          if (
            !/^Too many failed login attempts\. Account locked\. Please try After \d+ mins$/.test(
              message
            )
          ) {
            toast.error(message);
          }
        } else {
          // Fallback for unknown error formats
          toast.error('Something went wrong');
        }
        // Show the toast, but still reject - returning a plain value here
        // resolves the *original* request's promise with that value instead
        // of the expected { data: { data: ... } } shape, so every caller
        // that reads response?.data?.data silently gets undefined and pages
        // downstream (e.g. table/list components) can crash on it.
        return Promise.reject(error);
      }

      // The request was made and the server responded with a status code that falls out of the range of 2xx
      switch (error.response.status) {
        // Only a message of exactly 'Token expired' reaches this switch at
        // all - the branch above already rejected every other error message,
        // including other 401 variants (invalid credentials, invalid token),
        // with its own toast.
        case 401: {
          console.log('token expired and try to refresh token');
          // Several requests can hit this at once (a page fires them in
          // parallel) - share one refresh attempt instead of each request
          // racing its own, which would also thrash localStorage's token.
          if (!refreshPromise) {
            refreshPromise = (
              import.meta.env.VITE_APP_ENV === 'dev' ? refreshTokenDev() : refreshTokenProd()
            ).finally(() => {
              refreshPromise = null;
            });
          }

          try {
            const newAccessToken = await refreshPromise;
            console.log('This is the new AccessToken : ' + newAccessToken);

            if (newAccessToken) {
              const originalRequest = error.config;
              originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
              return axios(originalRequest);
            }
            // Refresh didn't throw, but produced no usable token - treat
            // this the same as a failed refresh below.
            console.log('Refresh returned no access token - logging out');
          } catch (refreshError) {
            // refresh token expired hence logout the user here
            console.log('Refresh Token Expired Logout User', refreshError);
          }

          redirectToLoginOnSessionExpiry();
          // We're about to hard-navigate to /login (full page reload), so
          // every component on the current page is moments from being torn
          // down anyway. Different endpoints nest their payload at different
          // depths (some callers read response.data.data, others
          // response.data.data.data), so there's no single fake "empty"
          // shape that's safe for all of them - rejecting would also make
          // every in-flight page fire its own "Error fetching X" toast on
          // top of the one clear message above. Returning a promise that
          // never settles avoids both: nothing else runs before the redirect.
          return new Promise(() => {});
        }
        case 403:
          // Handle forbidden error
          console.log('Forbidden');
          break;
        case 404:
          // Handle not found error

          console.log('Not Found');
          break;
        case 500:
          // Handle server error
          console.log('Internal Server Error');
          break;
        default:
          // Handle other errors
          console.log('An error occurred', error.response.status);
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.log('No response received');
    } else {
      // Something happened in setting up the request that triggered an Error
      console.log('Error', error.message);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
